// src/components/GoogleAnalytics.js
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { safeLog } from '../utils/hydrationSafe';
import { trackPageViewWithConsent, isAnalyticsEnabled } from '../utils/analyticsWithConsentMode';
import { getCookiePreferences } from '../utils/cookieManager';

/**
 * GDPR-compliant Google Analytics component with Consent Mode
 * Uses Consent Mode v2 to track even before full consent (with restricted data)
 * Upgrades tracking when user explicitly consents
 * This should be included once in your app (typically in App.js)
 */
const GoogleAnalytics = () => {
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const sentPageViewRef = useRef(false);
  
  // Wait briefly for analytics to load (it's pre-loaded in index.html)
  useEffect(() => {
    const checkAnalyticsReady = () => {
      if (window.gtag) {
        setIsReady(true);
      } else {
        // Retry a few times if gtag not immediately available
        const timer = setTimeout(checkAnalyticsReady, 100);
        return () => clearTimeout(timer);
      }
    };
    
    checkAnalyticsReady();
  }, []);
  
  // Track pageviews using Consent Mode v2
  // Works with restricted data before full consent, upgrades when user consents
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    
    // Don't track same page twice (handles route changes correctly)
    if (sentPageViewRef.current && location.pathname + location.search === sentPageViewRef.current) {
      return;
    }
    
    // Wait for analytics to be ready
    if (!isReady || !window.gtag) {
      return;
    }
    
    // Format the path to ensure consistency (e.g., remove trailing slash)
    const path = location.pathname.replace(/\/$/, '') + location.search;
    sentPageViewRef.current = path;
    
    // Get more detailed page information (safely access browser APIs)
    const pageTitle = (typeof document !== 'undefined' && document.title) || 'OriginSelect';
    const countryCode = location.pathname.includes('/country/') ? 
      location.pathname.split('/country/')[1].split('/')[0] : 
      'global'; // Default to global if not specified
      
    // Use Consent Mode v2 tracking (works even with restricted consent)
    trackPageViewWithConsent(pageTitle, window.location?.href || '');
    
    // For debug purposes - only log in development and not during prerendering
    const pageType = getPageType(location.pathname);
    const consentStatus = isAnalyticsEnabled() ? 'granted' : 'restricted';
    safeLog('GA pageview tracked (with consent):', path, {
      country: countryCode, 
      page_type: pageType,
      consent_status: consentStatus
    });
  }, [location.pathname, location.search, isReady]);

  // Helper function to determine page type for better analytics categorization
  const getPageType = (pathname) => {
    if (pathname.includes('/home')) return 'home';
    if (pathname.includes('/privacy')) return 'legal';
    if (pathname.includes('/disclaimer')) return 'legal';
    if (pathname.includes('/accessibility')) return 'legal';
    if (pathname.includes('/products/')) return 'product_detail';
    if (pathname.includes('/products')) return 'product_list';
    if (pathname.includes('/search')) return 'search';
    if (pathname.includes('/about')) return 'about';
    if (pathname.includes('/contact')) return 'contact';
    if (pathname.includes('/login')) return 'account';
    if (pathname.includes('/chat')) return 'engagement';
    if (pathname.includes('/favorites')) return 'engagement';
    return 'other';
  };

  // This component doesn't render anything
  return null;
};

export default GoogleAnalytics;
